import { useState, type FormEvent } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  KeyRound,
  ShieldCheck
} from 'lucide-react';
import { useNavigate } from '../../app/router';
import { Logo } from '../../components/Logo';
import { ApiError, apiRequest } from '../../lib/api';
import { saveToken, superadminTokenKey } from '../../app/session';

type LoginResponse =
  | {
      status: 'MFA_REQUIRED';
      challengeToken: string;
      user: { email: string; fullName: string };
    }
  | {
      status: 'MFA_SETUP_REQUIRED';
      setupToken: string;
      secret: string;
      enrollmentUrl: string;
      qrDataUrl: string;
      user: { email: string; fullName: string };
    };

interface SessionResponse {
  accessToken: string;
  recoveryCodes?: string[];
}

type Step = 'credentials' | 'verify' | 'setup' | 'recovery';

export function SuperadminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [secret, setSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [pendingAccessToken, setPendingAccessToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function resetToCredentials() {
    setStep('credentials');
    setPassword('');
    setMfaCode('');
    setMfaToken('');
    setSecret('');
    setQrDataUrl('');
    setError('');
  }

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await apiRequest<LoginResponse>(
        '/api/v1/superadmin/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password })
        }
      );
      setMfaCode('');
      if (response.status === 'MFA_SETUP_REQUIRED') {
        setMfaToken(response.setupToken);
        setSecret(response.secret);
        setQrDataUrl(response.qrDataUrl);
        setStep('setup');
      } else {
        setMfaToken(response.challengeToken);
        setStep('verify');
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Prijava nije dostupna.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function submitMfa(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint =
        step === 'setup'
          ? '/api/v1/superadmin/auth/mfa/activate'
          : '/api/v1/superadmin/auth/mfa/verify';
      const response = await apiRequest<SessionResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ token: mfaToken, code: mfaCode })
      });
      if (response.recoveryCodes?.length) {
        setPendingAccessToken(response.accessToken);
        setRecoveryCodes(response.recoveryCodes);
        setRecoveryConfirmed(false);
        setStep('recovery');
        return;
      }
      saveToken(superadminTokenKey, response.accessToken);
      navigate('/superadmin', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Sigurnosna provjera nije dostupna.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function copyRecoveryCodes() {
    await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  function finishEnrollment() {
    if (!recoveryConfirmed || !pendingAccessToken) return;
    saveToken(superadminTokenKey, pendingAccessToken);
    navigate('/superadmin', { replace: true });
  }

  return (
    <main className="admin-login">
      <section className="login-card admin-login__card">
        <Logo />
        <span className="admin-badge">
          <ShieldCheck size={16} /> Zaštićeni vlasnički pristup
        </span>

        {step === 'credentials' && (
          <form onSubmit={submitCredentials}>
            <h1>Superadmin</h1>
            <p className="muted">
              Firme, licence, moduli i tehničko stanje platforme.
            </p>
            <label className="field">
              <span>E-mail</span>
              <span className="field__control">
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </span>
            </label>
            <label className="field">
              <span>Lozinka</span>
              <span className="field__control">
                <input
                  type="password"
                  autoComplete="current-password"
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </span>
            </label>
            {error && <div className="form-error">{error}</div>}
            <button
              className="button button--primary button--large"
              disabled={loading}
            >
              {loading ? 'Provjeravam…' : 'Nastavi'} <ArrowRight size={18} />
            </button>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={submitMfa}>
            <h1>Sigurnosna provjera</h1>
            <p className="muted">
              Upišite šesteroznamenkasti kod iz autentifikacijske aplikacije ili
              jedan spremljeni pričuvni kod.
            </p>
            <label className="field">
              <span>Autentifikacijski kod</span>
              <span className="field__control">
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  placeholder="000000 ili XXXX-XXXX-XXXX-XXXX"
                  autoFocus
                  required
                />
              </span>
            </label>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={resetToCredentials}
                disabled={loading}
              >
                <ArrowLeft size={17} /> Natrag
              </button>
              <button
                className="button button--primary button--large"
                disabled={loading}
              >
                {loading ? 'Provjeravam…' : 'Potvrdi prijavu'}
                <ShieldCheck size={18} />
              </button>
            </div>
          </form>
        )}

        {step === 'setup' && (
          <form onSubmit={submitMfa}>
            <h1>Uključite dvofaktorsku zaštitu</h1>
            <p className="muted">
              Skenirajte QR kod aplikacijom Microsoft Authenticator, Google
              Authenticator, 1Password ili drugom TOTP aplikacijom.
            </p>
            <div className="mfa-setup">
              {qrDataUrl && (
                <img
                  className="mfa-setup__qr"
                  src={qrDataUrl}
                  alt="QR kod za povezivanje autentifikacijske aplikacije"
                />
              )}
              <div className="mfa-setup__manual">
                <strong>Ručni ključ</strong>
                <code>{secret}</code>
                <small>
                  Ključ čuvajte privatnim. Nakon povezivanja upišite kod koji
                  aplikacija prikazuje.
                </small>
              </div>
            </div>
            <label className="field">
              <span>Prvi šesteroznamenkasti kod</span>
              <span className="field__control">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </span>
            </label>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={resetToCredentials}
                disabled={loading}
              >
                <ArrowLeft size={17} /> Odustani
              </button>
              <button
                className="button button--primary button--large"
                disabled={loading}
              >
                {loading ? 'Uključujem…' : 'Uključi zaštitu'}
                <KeyRound size={18} />
              </button>
            </div>
          </form>
        )}

        {step === 'recovery' && (
          <div>
            <h1>Spremite pričuvne kodove</h1>
            <p className="muted">
              Svaki kod može se iskoristiti samo jednom kada nemate pristup
              autentifikacijskoj aplikaciji. Nakon zatvaranja više ih nećemo
              prikazati.
            </p>
            <div className="recovery-codes" aria-label="Pričuvni kodovi">
              {recoveryCodes.map((code) => (
                <code key={code}>{code}</code>
              ))}
            </div>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => void copyRecoveryCodes()}
            >
              {copied ? <Check size={17} /> : <Copy size={17} />}
              {copied ? 'Kopirano' : 'Kopiraj sve kodove'}
            </button>
            <label className="checkbox-field mfa-recovery-confirm">
              <input
                type="checkbox"
                checked={recoveryConfirmed}
                onChange={(event) => setRecoveryConfirmed(event.target.checked)}
              />
              <span>Spremio sam pričuvne kodove na sigurno mjesto.</span>
            </label>
            <button
              type="button"
              className="button button--primary button--large"
              disabled={!recoveryConfirmed}
              onClick={finishEnrollment}
            >
              Otvori upravljanje <ArrowRight size={18} />
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
